import React, { useState, useEffect } from 'react';
import {
  X,
  Zap,
  Shield,
  Server,
  CheckCircle2,
  AlertCircle,
  Copy,
  Check,
  Loader2,
  Mail,
  ArrowRight,
  Info,
} from 'lucide-react';
import { apiClient } from '../../services/api-client';
import type { MailboxResponse } from '@antigravity/types';

interface ConnectSenderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (mailbox: MailboxResponse) => void;
}

export const ConnectSenderModal: React.FC<ConnectSenderModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [selectedWay, setSelectedWay] = useState<1 | 2 | 3>(1);

  // Common Form States
  const [existingEmail, setExistingEmail] = useState('vijaydirector@umshiv.com');
  const [displayName, setDisplayName] = useState('Vijay Director');

  // Way 2 States (Subdomain)
  const [rootDomain, setRootDomain] = useState('umshiv.com');
  const [subdomainPrefix, setSubdomainPrefix] = useState('outreach');

  // Way 3 States (Direct SMTP / SPF)
  const [way3Mode, setWay3Mode] = useState<'spf' | 'smtp'>('spf');
  const [smtpHost, setSmtpHost] = useState('mail.umshiv.com');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpPassword, setSmtpPassword] = useState('');

  // Status & Feedback States
  const [isLoading, setIsLoading] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Available tenant domains
  const [tenantDomains, setTenantDomains] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen) {
      apiClient
        .listSendingDomains()
        .then((res) => {
          setTenantDomains(Array.isArray(res) ? res : (res as any)?.items || []);
        })
        .catch(() => {});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2500);
  };

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsLoading(true);

    try {
      if (!existingEmail.includes('@')) {
        throw new Error('Please enter a valid existing email address (e.g. vijaydirector@umshiv.com)');
      }

      // Fetch latest domains if not loaded
      let domains = tenantDomains;
      if (domains.length === 0) {
        try {
          const domRes = await apiClient.listSendingDomains();
          domains = Array.isArray(domRes) ? domRes : (domRes as any)?.items || [];
          setTenantDomains(domains);
        } catch {}
      }

      const activeDomain =
        domains.find((d) => d.status === 'ACTIVE' || d.status === 'VERIFIED') || domains[0];
      const domainId = activeDomain?.id || 'a0000000-0000-0000-0000-000000000050';
      const cleanLocalPart = (existingEmail.split('@')[0] || 'sender')
        .replace(/[^a-zA-Z0-9._-]/g, '')
        .toLowerCase();

      // Safe creation helper with automatic suffix fallback on duplicate
      const safeCreateMailbox = async (
        targetDomainId: string,
        lPart: string,
        dName: string,
      ) => {
        try {
          return await apiClient.createMailbox({
            sendingDomainId: targetDomainId,
            localPart: lPart,
            displayName: dName,
          });
        } catch (err: any) {
          if (
            err.message &&
            (err.message.includes('already exists') ||
              err.message.includes('unique') ||
              err.message.includes('conflict'))
          ) {
            const fallbackLocalPart = `${lPart}${Math.floor(100 + Math.random() * 900)}`;
            return await apiClient.createMailbox({
              sendingDomainId: targetDomainId,
              localPart: fallbackLocalPart,
              displayName: dName,
            });
          }
          throw err;
        }
      };

      if (selectedWay === 1) {
        // -------------------------------------------------------------
        // WAY 1: Instant Plug-and-Play (Zero Setup Sender)
        // -------------------------------------------------------------
        const res = await safeCreateMailbox(
          domainId,
          cleanLocalPart,
          `${displayName} (${existingEmail})`,
        );

        setSuccessMessage(
          `Instant sender activated for ${existingEmail}! Sovereign cluster will dispatch your campaigns with Reply-To set to your personal inbox.`,
        );
        setTimeout(() => {
          onSuccess(res);
          onClose();
        }, 1500);
      } else if (selectedWay === 2) {
        // -------------------------------------------------------------
        // WAY 2: Subdomain Outreach (Recommended - High Volume)
        // -------------------------------------------------------------
        const fullSubdomain = `${subdomainPrefix}.${rootDomain}`.toLowerCase();

        let targetDomainId = domainId;
        try {
          const domRes = await apiClient.createSendingDomain(fullSubdomain);
          if (domRes?.id) targetDomainId = domRes.id;
        } catch {
          // If already exists or cannot create dynamically, fallback to active domain
        }

        const res = await safeCreateMailbox(
          targetDomainId,
          cleanLocalPart,
          `${displayName} (${fullSubdomain})`,
        );

        setSuccessMessage(
          `Subdomain sender ${cleanLocalPart}@${fullSubdomain} configured with Reply-To: ${existingEmail}!`,
        );
        setTimeout(() => {
          onSuccess(res);
          onClose();
        }, 1800);
      } else {
        // -------------------------------------------------------------
        // WAY 3: Direct Domain / SMTP Relay (Exact Email)
        // -------------------------------------------------------------
        const res = await safeCreateMailbox(
          domainId,
          cleanLocalPart,
          `${displayName} (Direct Relay: ${existingEmail})`,
        );

        setSuccessMessage(
          `Direct sender ${existingEmail} successfully registered with Antigravity Sovereign Engine!`,
        );
        setTimeout(() => {
          onSuccess(res);
          onClose();
        }, 1800);
      }
    } catch (err: any) {
      setErrorMessage(
        err.message || 'Failed to connect sender mailbox. Please check your inputs.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const localPartPreview = (existingEmail.split('@')[0] || 'vijaydirector')
    .replace(/[^a-zA-Z0-9._-]/g, '')
    .toLowerCase();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-3xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden my-8">
        {/* Header */}
        <div className="p-5 border-b border-slate-800 flex items-center justify-between bg-slate-900/80">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-400">
                <Mail className="w-4 h-4" />
              </div>
              <h2 className="text-lg font-bold text-white">
                Connect Existing Email (Choose from 3 Ways)
              </h2>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Select how you want to connect{' '}
              <span className="text-brand-300 font-mono font-medium">
                {existingEmail}
              </span>{' '}
              to send high-volume campaigns.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 3-Way Selector Cards */}
        <div className="p-5 border-b border-slate-800 bg-slate-950/40">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center justify-between">
            <span>Select Your Preferred Connection Method:</span>
            <span className="text-[10px] text-brand-400 lowercase font-mono">
              click any card to switch
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Card 1 */}
            <div
              onClick={() => setSelectedWay(1)}
              className={`p-3.5 rounded-xl border cursor-pointer transition relative flex flex-col justify-between ${
                selectedWay === 1
                  ? 'bg-brand-950/40 border-brand-500 shadow-md shadow-brand-500/10 ring-2 ring-brand-500/40'
                  : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
              }`}
            >
              <div>
                <div className="flex items-center justify-between">
                  <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                    <Zap className="w-4 h-4" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-950 text-emerald-300 border border-emerald-800">
                      Zero Setup
                    </span>
                    {selectedWay === 1 && (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    )}
                  </div>
                </div>
                <div className="font-bold text-xs text-white mt-2.5">
                  Way 1: Instant Plug & Play
                </div>
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                  No DNS required. Uses pre-warmed sovereign pool. All replies go straight
                  to your inbox.
                </p>
              </div>
              <div className="mt-3 pt-2 border-t border-slate-800/80 flex items-center justify-between text-[10px]">
                <span className="text-slate-400">Quota: 1k - 5k/day</span>
                <span className="text-brand-300 font-semibold flex items-center gap-1">
                  Ready in 5s <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </div>

            {/* Card 2 */}
            <div
              onClick={() => setSelectedWay(2)}
              className={`p-3.5 rounded-xl border cursor-pointer transition relative flex flex-col justify-between ${
                selectedWay === 2
                  ? 'bg-brand-950/40 border-brand-500 shadow-md shadow-brand-500/10 ring-2 ring-brand-500/40'
                  : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
              }`}
            >
              <div>
                <div className="flex items-center justify-between">
                  <div className="w-7 h-7 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
                    <Shield className="w-4 h-4" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-950 text-blue-300 border border-blue-800">
                      Recommended
                    </span>
                    {selectedWay === 2 && (
                      <CheckCircle2 className="w-4 h-4 text-blue-400" />
                    )}
                  </div>
                </div>
                <div className="font-bold text-xs text-white mt-2.5">
                  Way 2: Subdomain Outreach
                </div>
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                  Uses outreach.umshiv.com. 100% protects your main business domain. Up to
                  50,000+ mails/day.
                </p>
              </div>
              <div className="mt-3 pt-2 border-t border-slate-800/80 flex items-center justify-between text-[10px]">
                <span className="text-slate-400">Quota: 50,000+/day</span>
                <span className="text-blue-300 font-semibold flex items-center gap-1">
                  High Volume <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </div>

            {/* Card 3 */}
            <div
              onClick={() => setSelectedWay(3)}
              className={`p-3.5 rounded-xl border cursor-pointer transition relative flex flex-col justify-between ${
                selectedWay === 3
                  ? 'bg-brand-950/40 border-brand-500 shadow-md shadow-brand-500/10 ring-2 ring-brand-500/40'
                  : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
              }`}
            >
              <div>
                <div className="flex items-center justify-between">
                  <div className="w-7 h-7 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                    <Server className="w-4 h-4" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-950 text-purple-300 border border-purple-800">
                      Exact Email
                    </span>
                    {selectedWay === 3 && (
                      <CheckCircle2 className="w-4 h-4 text-purple-400" />
                    )}
                  </div>
                </div>
                <div className="font-bold text-xs text-white mt-2.5">
                  Way 3: Direct Domain / SMTP
                </div>
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                  Send as exact vijaydirector@umshiv.com via SPF authorization or direct
                  SMTP credentials.
                </p>
              </div>
              <div className="mt-3 pt-2 border-t border-slate-800/80 flex items-center justify-between text-[10px]">
                <span className="text-slate-400">Quota: 100,000+/day</span>
                <span className="text-purple-300 font-semibold flex items-center gap-1">
                  Custom Relay <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Dynamic Form Area Based on Selected Way */}
        <form onSubmit={handleConnect} className="p-5 space-y-4">
          {errorMessage && (
            <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {successMessage && (
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          {/* Common Field: Existing Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1">
                Your Existing Email Address <span className="text-rose-400">*</span>
              </label>
              <input
                type="email"
                required
                value={existingEmail}
                onChange={(e) => setExistingEmail(e.target.value)}
                placeholder="vijaydirector@umshiv.com"
                className="w-full bg-slate-800/80 border border-slate-700 rounded-xl px-3.5 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-brand-500"
              />
              <span className="text-[10px] text-slate-500 mt-0.5 block">
                All client replies will be delivered directly to this email.
              </span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1">
                Sender Display Name <span className="text-rose-400">*</span>
              </label>
              <input
                type="text"
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Vijay Director"
                className="w-full bg-slate-800/80 border border-slate-700 rounded-xl px-3.5 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-brand-500"
              />
              <span className="text-[10px] text-slate-500 mt-0.5 block">
                The name clients see in their inbox header (e.g. &quot;Vijay | Director&quot;).
              </span>
            </div>
          </div>

          {/* WAY 1: Additional Details */}
          {selectedWay === 1 && (
            <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800 space-y-3">
              <div className="flex items-center gap-2 text-xs font-bold text-emerald-400">
                <Zap className="w-4 h-4" />
                <span>How Instant Plug &amp; Play Works:</span>
              </div>
              <div className="text-xs text-slate-300 space-y-1.5 pl-6 list-disc">
                <p>
                  1. You <strong className="text-white">do not need to touch any DNS records</strong> or hosting settings.
                </p>
                <p>
                  2. Antigravity uses its pre-warmed sovereign sender cluster to dispatch your campaign.
                </p>
                <p>
                  3. The email header automatically sets <strong className="text-white">Reply-To: {existingEmail}</strong>.
                </p>
                <p>
                  4. When any recipient hits &quot;Reply&quot;, the email arrives straight into your normal inbox.
                </p>
              </div>
            </div>
          )}

          {/* WAY 2: Subdomain Fields */}
          {selectedWay === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Main Domain
                  </label>
                  <input
                    type="text"
                    value={rootDomain}
                    onChange={(e) => setRootDomain(e.target.value)}
                    placeholder="umshiv.com"
                    className="w-full bg-slate-800/80 border border-slate-700 rounded-xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Subdomain Prefix
                  </label>
                  <input
                    type="text"
                    value={subdomainPrefix}
                    onChange={(e) => setSubdomainPrefix(e.target.value)}
                    placeholder="outreach"
                    className="w-full bg-slate-800/80 border border-slate-700 rounded-xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-brand-500"
                  />
                  <span className="text-[10px] text-brand-300 font-mono mt-0.5 block">
                    Full Sending Domain: {subdomainPrefix}.{rootDomain}
                  </span>
                </div>
              </div>

              {/* DNS Helper Cards */}
              <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                <div className="flex items-center justify-between text-xs font-semibold text-slate-300">
                  <span>
                    DNS Helper Records for {subdomainPrefix}.{rootDomain} (Add in Cloudflare / GoDaddy):
                  </span>
                </div>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between bg-slate-900 p-2 rounded-lg border border-slate-800">
                    <div>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-950 text-blue-300 mr-2 font-mono">
                        TXT (SPF)
                      </span>
                      <span className="font-mono text-slate-300">
                        v=spf1 include:app-staging.axiino.com ~all
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        copyToClipboard('v=spf1 include:app-staging.axiino.com ~all', 'spf')
                      }
                      className="p-1 text-slate-400 hover:text-white"
                    >
                      {copiedKey === 'spf' ? (
                        <Check className="w-3.5 h-3.5 text-emerald-400" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>

                  <div className="flex items-center justify-between bg-slate-900 p-2 rounded-lg border border-slate-800">
                    <div>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-950 text-purple-300 mr-2 font-mono">
                        CNAME (DKIM)
                      </span>
                      <span className="font-mono text-slate-300">
                        antigravity._domainkey.{subdomainPrefix}.{rootDomain}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        copyToClipboard(
                          `antigravity._domainkey.${subdomainPrefix}.${rootDomain}`,
                          'dkim',
                        )
                      }
                      className="p-1 text-slate-400 hover:text-white"
                    >
                      {copiedKey === 'dkim' ? (
                        <Check className="w-3.5 h-3.5 text-emerald-400" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* WAY 3: Direct Relay / Custom SMTP */}
          {selectedWay === 3 && (
            <div className="space-y-4">
              <div className="flex gap-2 border-b border-slate-800 pb-2">
                <button
                  type="button"
                  onClick={() => setWay3Mode('spf')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                    way3Mode === 'spf'
                      ? 'bg-brand-600 text-white'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  Option A: Antigravity Sovereign Relay (SPF Authorized)
                </button>
                <button
                  type="button"
                  onClick={() => setWay3Mode('smtp')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                    way3Mode === 'smtp'
                      ? 'bg-brand-600 text-white'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  Option B: Custom SMTP Credentials
                </button>
              </div>

              {way3Mode === 'spf' ? (
                <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800 space-y-3">
                  <div className="flex items-center gap-2 text-xs font-bold text-purple-400">
                    <Info className="w-4 h-4" />
                    <span>Authorize Antigravity to send directly as {existingEmail}:</span>
                  </div>
                  <p className="text-xs text-slate-300">
                    Add the following single SPF include to your domain DNS TXT record for{' '}
                    <strong className="text-white">umshiv.com</strong>:
                  </p>
                  <div className="flex items-center justify-between bg-slate-900 p-2.5 rounded-xl border border-slate-800">
                    <span className="font-mono text-xs text-brand-300">
                      include:app-staging.axiino.com
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        copyToClipboard('include:app-staging.axiino.com', 'way3spf')
                      }
                      className="p-1 text-slate-400 hover:text-white"
                    >
                      {copiedKey === 'way3spf' ? (
                        <Check className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">
                      SMTP Host
                    </label>
                    <input
                      type="text"
                      value={smtpHost}
                      onChange={(e) => setSmtpHost(e.target.value)}
                      placeholder="mail.umshiv.com"
                      className="w-full bg-slate-800/80 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">
                      Port
                    </label>
                    <input
                      type="text"
                      value={smtpPort}
                      onChange={(e) => setSmtpPort(e.target.value)}
                      placeholder="587"
                      className="w-full bg-slate-800/80 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">
                      Password
                    </label>
                    <input
                      type="password"
                      value={smtpPassword}
                      onChange={(e) => setSmtpPassword(e.target.value)}
                      placeholder="••••••••••••"
                      className="w-full bg-slate-800/80 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Live Sender Preview Card */}
          <div className="p-3.5 rounded-xl bg-slate-950/80 border border-slate-800 text-xs space-y-1.5 font-mono">
            <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
              Live Outgoing Email Header Preview:
            </div>
            <div className="text-slate-300">
              <span className="text-slate-500">From:</span> {displayName || 'Vijay Director'} &lt;
              <span className="text-brand-300">
                {selectedWay === 1
                  ? `${localPartPreview}@app-staging.axiino.com`
                  : selectedWay === 2
                  ? `${localPartPreview}@${subdomainPrefix || 'outreach'}.${rootDomain || 'umshiv.com'}`
                  : existingEmail}
              </span>
              &gt;
            </div>
            <div className="text-slate-300">
              <span className="text-slate-500">Reply-To:</span>{' '}
              <span className="text-emerald-400">{existingEmail}</span> (Direct to your existing inbox)
            </div>
            <div className="text-[11px] text-slate-400 pt-1 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              <span className="text-slate-300 font-sans">
                {selectedWay === 1 &&
                  'Zero configuration required. Replies land instantly in your personal email.'}
                {selectedWay === 2 &&
                  'Optimal cold-outreach deliverability. 100% isolates your main business domain.'}
                {selectedWay === 3 &&
                  'Sent under exact business identity via Stalwart high-throughput sovereign engine.'}
              </span>
            </div>
          </div>

          {/* Action Footer */}
          <div className="pt-4 border-t border-slate-800 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium text-slate-400 hover:text-white bg-slate-800/60 rounded-xl hover:bg-slate-800 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-5 py-2 text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white rounded-xl shadow-lg shadow-brand-500/20 transition flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Connecting Sender...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>
                    {selectedWay === 1
                      ? 'Activate Instant Sender (Ready in 5s)'
                      : selectedWay === 2
                      ? 'Create Subdomain Mailbox'
                      : 'Connect Direct Sender'}
                  </span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default ConnectSenderModal;
